import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft,
  Loader2,
  MapPin,
  Music,
  ExternalLink,
  Instagram,
  Youtube,
  Twitter,
  Globe,
  Calendar,
  Play,
} from 'lucide-react';
import api, { resolveImageUrl } from '../lib/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const socialConfig = [
  { key: 'spotify', label: 'Spotify', Icon: Music, color: '#1DB954' },
  { key: 'youtube', label: 'YouTube', Icon: Youtube, color: '#FF0033' },
  { key: 'instagram', label: 'Instagram', Icon: Instagram, color: '#E1306C' },
  { key: 'tiktok', label: 'TikTok', Icon: Music, color: '#69C9D0' },
  { key: 'twitter', label: 'Twitter/X', Icon: Twitter, color: '#ffffff' },
  { key: 'website', label: 'Website', Icon: Globe, color: '#E9C46A' },
];

const ArtistDetailPage = () => {
  const { slug } = useParams();
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    setError(false);
    api
      .get(`/artists/${slug}`)
      .then((r) => setArtist(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  const metaDescription = artist
    ? (artist.bio || `${artist.name} — artis binaan Werdi Media`).slice(0, 160)
    : '';
  const metaImage = artist?.photo ? resolveImageUrl(artist.photo) : '';
  const canonicalUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <div className="App bg-[#0a0a0a] min-h-screen" data-testid="artist-detail-page">
      {artist && (
        <Helmet>
          <title>{`${artist.name} — Artis Werdi Media`}</title>
          <meta name="description" content={metaDescription} />
          <link rel="canonical" href={canonicalUrl} />
          <meta property="og:type" content="profile" />
          <meta property="og:title" content={`${artist.name} — Werdi Media`} />
          <meta property="og:description" content={metaDescription} />
          <meta property="og:url" content={canonicalUrl} />
          {metaImage && <meta property="og:image" content={metaImage} />}
          <meta name="twitter:card" content={metaImage ? 'summary_large_image' : 'summary'} />
          <meta name="twitter:title" content={`${artist.name} — Werdi Media`} />
          <meta name="twitter:description" content={metaDescription} />
          {metaImage && <meta name="twitter:image" content={metaImage} />}
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'MusicGroup',
              name: artist.name,
              genre: artist.genre,
              image: metaImage || undefined,
              description: metaDescription,
              url: canonicalUrl,
              sameAs: Object.values(artist.socials || {}).filter(Boolean),
              album: (artist.discography || []).map((d) => ({
                '@type': 'MusicAlbum',
                name: d.title,
                datePublished: d.release_date || undefined,
                image: d.cover ? resolveImageUrl(d.cover) : undefined,
                url: d.url || undefined,
              })),
            })}
          </script>
        </Helmet>
      )}
      <Navbar />

      {loading ? (
        <div className="flex justify-center py-40">
          <Loader2 className="w-6 h-6 text-[#E9C46A] animate-spin" />
        </div>
      ) : error || !artist ? (
        <div className="pt-40 pb-24 text-center px-6">
          <p className="text-neutral-400 text-xl mb-6">Artis tidak ditemukan.</p>
          <Link
            to="/artis"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D4A94F] text-black font-semibold"
          >
            <ArrowLeft className="w-4 h-4" /> Kembali ke Roster
          </Link>
        </div>
      ) : (
        <>
          {/* Hero */}
          <section className="relative pt-28 pb-16 lg:pt-36 lg:pb-20 overflow-hidden">
            {artist.photo && (
              <div className="absolute inset-0 -z-10">
                <img
                  src={resolveImageUrl(artist.photo)}
                  alt=""
                  className="w-full h-full object-cover opacity-25 blur-2xl scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/80 to-black" />
              </div>
            )}
            <div className="max-w-6xl mx-auto px-6 lg:px-10">
              <Link
                to="/artis"
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-neutral-500 hover:text-[#E9C46A] link-transition mb-10"
              >
                <ArrowLeft className="w-3 h-3" /> Semua Artis
              </Link>

              <div className="grid md:grid-cols-[320px_1fr] gap-10 items-start">
                <div className="rounded-3xl overflow-hidden border border-[#D4A94F]/25 bg-neutral-900 aspect-square shadow-[0_25px_60px_-20px_rgba(212,169,79,0.35)]">
                  {artist.photo ? (
                    <img
                      src={resolveImageUrl(artist.photo)}
                      alt={artist.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Music className="w-16 h-16 text-neutral-700" />
                    </div>
                  )}
                </div>

                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-widest text-[#E9C46A] font-semibold mb-3">
                    Werdi Media Artist Roster
                  </p>
                  <h1 className="text-white font-bold text-5xl md:text-6xl leading-[1.05] tracking-tight mb-4 break-words">
                    {artist.name}
                  </h1>
                  <div className="flex flex-wrap items-center gap-3 mb-6">
                    <span className="text-[10px] tracking-widest uppercase font-bold text-black bg-[#D4A94F] px-3 py-1 rounded-full">
                      {artist.genre}
                    </span>
                    {artist.hometown && (
                      <span className="text-sm text-neutral-400 flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5" /> {artist.hometown}
                      </span>
                    )}
                    <span className="text-sm text-neutral-400 flex items-center gap-1.5">
                      <Music className="w-3.5 h-3.5" />
                      {(artist.discography || []).length} rilisan
                    </span>
                  </div>

                  {artist.bio && (
                    <p className="text-neutral-300 text-base md:text-lg leading-relaxed mb-8 break-words max-w-3xl">
                      {artist.bio}
                    </p>
                  )}

                  {/* Socials */}
                  <div className="flex flex-wrap gap-2">
                    {socialConfig.map(({ key, label, Icon }) => {
                      const url = artist.socials?.[key];
                      if (!url) return null;
                      return (
                        <a
                          key={key}
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.04] border border-white/10 hover:border-[#D4A94F]/50 text-sm text-neutral-200 hover:text-white btn-transition"
                          data-testid={`artist-social-${key}`}
                        >
                          <Icon className="w-3.5 h-3.5" />
                          {label}
                          <ExternalLink className="w-3 h-3 opacity-60" />
                        </a>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Discography */}
          {(artist.discography || []).length > 0 && (
            <section className="pb-24">
              <div className="max-w-6xl mx-auto px-6 lg:px-10">
                <div className="flex items-baseline justify-between mb-8">
                  <h2 className="text-white font-bold text-2xl md:text-3xl tracking-tight">
                    <span className="gold-gradient-text">Discography</span>
                  </h2>
                  <span className="text-neutral-500 text-sm">
                    {artist.discography.length} rilis
                  </span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="artist-discography">
                  {artist.discography.map((d, idx) => (
                    <a
                      key={idx}
                      href={d.url || '#'}
                      target={d.url ? '_blank' : undefined}
                      rel="noreferrer"
                      onClick={(e) => !d.url && e.preventDefault()}
                      className={`group relative rounded-2xl overflow-hidden bg-[#111] border border-white/[0.06] hover:border-[#D4A94F]/40 card-transition ${
                        !d.url ? 'cursor-default' : ''
                      }`}
                    >
                      <div className="aspect-square relative overflow-hidden bg-neutral-900">
                        {d.cover ? (
                          <img
                            src={resolveImageUrl(d.cover)}
                            alt={d.title}
                            className="w-full h-full object-cover btn-transition group-hover:scale-105"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Music className="w-8 h-8 text-neutral-700" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent opacity-60" />
                        {d.url && (
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 btn-transition">
                            <div className="w-12 h-12 rounded-full bg-[#D4A94F] flex items-center justify-center shadow-[0_10px_30px_-5px_rgba(212,169,79,0.6)]">
                              <Play className="w-5 h-5 text-black fill-black ml-0.5" />
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h4 className="text-white font-semibold text-sm leading-tight truncate">
                          {d.title}
                        </h4>
                        {d.release_date && (
                          <p className="text-neutral-500 text-xs mt-1 flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> {fmtDate(d.release_date)}
                          </p>
                        )}
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </section>
          )}
        </>
      )}

      <Footer />
    </div>
  );
};

export default ArtistDetailPage;
